import { useState, useEffect, useCallback } from 'react'
import { axiosInstance } from '../../helpers/axios'
import {
    MoveOutDetail,
    FinalMoveOutStatus,
    FMOStatementListItem,
    FMOStatementDetail,
    StatementType,
} from '../../containers/MoveOut/types'

export type { FinalMoveOutStatus, StatementType }

interface UseFmoStatementsReturn {
    statements: FMOStatementListItem[]
    isLoading: boolean
    error: string | null
    refetch: () => void
}

export const useFmoStatements = (): UseFmoStatementsReturn => {
    const [statements, setStatements] = useState<FMOStatementListItem[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const fetchStatements = useCallback(async () => {
        setIsLoading(true)
        setError(null)
        try {
            const response = await axiosInstance.get<FMOStatementListItem[]>(
                'lease/tenant-fmo-statement/',
            )
            setStatements(response.data)
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : 'Failed to load FMO statements',
            )
        } finally {
            setIsLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchStatements()
    }, [fetchStatements])

    return { statements, isLoading, error, refetch: fetchStatements }
}

interface UseFmoStatementDetailReturn {
    moveOutDetail: MoveOutDetail | null
    statementType: StatementType | null
    pdfUrl: string | null
    sentDate: string | null
    isLoading: boolean
    error: string | null
}

export const useFmoStatementDetail = (
    statementId: number | null,
): UseFmoStatementDetailReturn => {
    const [moveOutDetail, setMoveOutDetail] = useState<MoveOutDetail | null>(
        null,
    )
    const [statementType, setStatementType] = useState<StatementType | null>(
        null,
    )
    const [pdfUrl, setPdfUrl] = useState<string | null>(null)
    const [sentDate, setSentDate] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (statementId === null) {
            setMoveOutDetail(null)
            setStatementType(null)
            setPdfUrl(null)
            setSentDate(null)
            return
        }

        const fetchDetail = async () => {
            setIsLoading(true)
            setError(null)
            try {
                const response = await axiosInstance.get<FMOStatementDetail>(
                    `lease/tenant-fmo-statement/${statementId}/`,
                )
                setMoveOutDetail(response.data.data)
                setStatementType(response.data.statement_type)
                setPdfUrl(response.data.pdf_url)
                setSentDate(response.data.sent_date)
            } catch (err) {
                setError(
                    err instanceof Error
                        ? err.message
                        : 'Failed to load move-out details',
                )
            } finally {
                setIsLoading(false)
            }
        }

        fetchDetail()
    }, [statementId])

    return { moveOutDetail, statementType, pdfUrl, sentDate, isLoading, error }
}

export const downloadFmoPdf = (pdfUrl: string, filename: string) => {
    const a = document.createElement('a')
    a.href = pdfUrl
    a.download = filename
    a.target = '_blank'
    a.rel = 'noopener noreferrer'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
}

export const printFmoPdf = (pdfUrl: string) => {
    const printWindow = window.open(pdfUrl)
    if (printWindow) {
        printWindow.addEventListener('load', () => {
            printWindow.print()
        })
    }
}
